import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { Brain, Download, Home, FileText, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CounselingReportView = ({ user }) => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAdminReport, setShowAdminReport] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  useEffect(() => {
    if (reportId) {
      fetchReport(reportId);
    }
  }, [reportId]);

  const fetchReport = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/counseling/report/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data);
    } catch (error) {
      toast.error('Failed to load report');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const downloadReport = (reportText, filename) => {
    const element = document.createElement('a');
    const file = new Blob([reportText], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = filename;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    toast.success('Report downloaded');
  };

  const downloadPdf = async () => {
    setDownloadingPdf(true);
    try {
      const token = localStorage.getItem('token');
      const isAdmin = localStorage.getItem('admin');
      const endpoint = showAdminReport && isAdmin 
        ? `${API}/counseling/report/${reportId}/download/admin`
        : `${API}/counseling/report/${reportId}/download/student`;
      
      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${showAdminReport ? 'Admin' : 'Student'}_Report_${report?.user_name?.replace(' ', '_') || 'Report'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF download error:', error);
      // Fallback to text download if PDF generation fails
      toast.error('PDF generation unavailable. Downloading as text file.');
      downloadReport(
        showAdminReport ? report.admin_report : report.student_report,
        `${showAdminReport ? 'Admin' : 'Student'}_Report_${report?.user_name?.replace(' ', '_') || 'Report'}.txt`
      );
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Brain className="w-16 h-16 text-indigo-500 animate-pulse" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-4">Report Not Found</h2>
          <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  const isAdmin = localStorage.getItem('admin');

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-teal-50">
      <nav className="bg-white/80 backdrop-blur-xl border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-500" />
            <h1 className="font-heading text-2xl font-bold text-slate-900">Career Report</h1>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                onClick={() => setShowAdminReport(!showAdminReport)}
                variant="outline"
                className="text-sm"
                data-testid="toggle-report-type-btn"
              >
                {showAdminReport ? 'View Student Report' : 'View Admin Report'}
              </Button>
            )}
            <Button
              onClick={downloadPdf}
              disabled={downloadingPdf}
              variant="outline"
              data-testid="download-pdf-btn"
              className="bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200"
            >
              {downloadingPdf ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Download PDF
                </>
              )}
            </Button>
            <Button
              onClick={() => downloadReport(
                showAdminReport ? report.admin_report : report.student_report,
                `${showAdminReport ? 'Admin' : 'Student'}_Report_${report.user_name.replace(' ', '_')}.txt`
              )}
              variant="ghost"
              data-testid="download-txt-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Text
            </Button>
            <Button onClick={() => navigate('/dashboard')} variant="ghost" data-testid="back-to-dashboard-btn">
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <Card className="bg-white rounded-2xl shadow-lg">
          <CardContent className="p-8">
            <div className="mb-8 p-6 bg-gradient-to-r from-indigo-50 to-teal-50 rounded-xl">
              <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                {report.recommendation}
              </h2>
              <div className="flex items-center gap-4 text-sm text-slate-600">
                <span>Track: {report.track === 'A' ? 'Stream Selection' : 'Career Mapping'}</span>
                <span>•</span>
                <span>Cognitive Score: {report.cognitive_score}%</span>
                <span>•</span>
                <span>{new Date(report.created_at).toLocaleDateString()}</span>
              </div>
            </div>

            <div className="prose prose-slate max-w-none">
              <ReactMarkdown>
                {showAdminReport && isAdmin ? report.admin_report : report.student_report}
              </ReactMarkdown>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CounselingReportView;
